import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
// ---- rsuite's STYLESHEET IS GONE, AND SO IS EVERY rsuite COMPONENT ----
//
// This imported `rsuite/dist/styles/rsuite-default.min.css` for the date
// pickers on the old `dashboard-reports` screen. That screen was folded into
// `dashboard-screen`, its DateRangePicker went with it, and nothing in `src`
// imports anything from 'rsuite' any more — no component, no type, no class
// name. The stylesheet stayed behind.
//
// It was not inert. Loaded AFTER this file's own `index.css`, its bare reset
// won on equal specificity and silently owned the app's base typography:
//
//     body { font-size: 14px; line-height: 1.42857143; color: #575757;
//            background-color: #fff; font-family: Apple-System, Arial, ... }
//
// Measured on the running app, `getComputedStyle(document.body)` reported 14px
// and #575757 — not the 16px / --ink / Inter that index.css declares and that
// the design system is written against. Every unstyled paragraph in the product
// inherited a vendor default from a library it no longer uses, and the white
// background is what showed through wherever the app shell did not paint.
//
// Deleting the import is the whole fix and it carries no risk to rsuite
// surfaces, because there are none. The `.rs-picker-menu` safety net in
// index.css went with it for the same reason.
//
// `rsuite` is still in package.json and is now an unused dependency. Left for a
// deliberate `npm uninstall` rather than removed here, because that rewrites the
// lockfile and belongs in its own change.
import './axios-setup';
import App from './App';
import reportWebVitals from './reportWebVitals';

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
// THE LOCATION PICKER'S AUDIT PAGE — NOT IN A NORMAL BUILD.
//
// `npm run audit:location` builds with REACT_APP_PICKER_HARNESS=1 and drives
// the real picker in Chromium, Firefox and WebKit (see
// src/audit-harness/location-picker-harness.tsx). In every other build the
// outer condition is a constant false and webpack never parses what is inside
// it: no branch, no import, and — the part that needs care — NO CHUNK.
//
// THAT ONLY HOLDS BECAUSE THE VARIABLE IS DEFINED. .env.production sets
// REACT_APP_PICKER_HARNESS=0, so the test below compiles to `"0" === "1"`,
// which webpack folds while it is still deciding what to bundle. Left
// undefined, CRA compiles it to a property read on an object, nothing is folded
// until minification, and by then the audit page's chunk has already been
// written into the build — unreachable, but shipped. That happened once; the
// audit now looks for the chunk itself in a normal build.
//
// The path test is NESTED rather than joined with && for the same reason: the
// outer `if` must be a constant on its own.
if (process.env.REACT_APP_PICKER_HARNESS === '1') {
  if (window.location.pathname === '/__picker-harness') {
    const PickerHarness = React.lazy(
      () => import('./audit-harness/location-picker-harness')
    );
    root.render(
      <React.Suspense fallback={null}>
        <PickerHarness />
      </React.Suspense>
    );
  } else {
    root.render(<App />);
  }
} else {
  root.render(<App />);
}

// The app-wide service worker stays DISABLED (2026-07-20). The network-first SW
// was intercepting navigations and failing ("Failed to convert value to
// 'Response'"), breaking /signup and /login in production. service-worker.js is
// now a self-destructing kill switch, so any copy already installed in a
// browser removes itself on the next load too.
//
// WHAT CHANGED, AND WHY THIS IS NOT A REGRESSION OF THAT FIX.
//
// Web Push needs a live service worker, so there is now exactly one: push-sw.js.
// It is registered ONLY when a user explicitly turns push on (see
// components/notifications/notification-preferences.tsx) and it has NO fetch
// handler — a fetch handler was the actual bug, and a worker without one cannot
// intercept a navigation at all.
//
// So the blanket "unregister everything" below had to become a filter rather
// than a sweep: as written it ran on every page load and would have removed the
// push worker moments after the user registered it, silently, with push then
// appearing to be on while no notification ever arrived.
const PUSH_SW_FILENAME = 'push-sw.js';

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .getRegistrations()
      .then(registrations =>
        registrations.forEach(registration => {
          const scriptUrl =
            registration.active?.scriptURL ||
            registration.installing?.scriptURL ||
            registration.waiting?.scriptURL ||
            '';
          if (scriptUrl.includes(PUSH_SW_FILENAME)) return;
          registration.unregister();
        })
      )
      .catch(() => {
        /* ignore */
      });
    // Caches are still cleared unconditionally: push-sw.js opens none, so
    // anything here belongs to the retired worker.
    if (window.caches) {
      caches
        .keys()
        .then(keys => keys.forEach(k => caches.delete(k)))
        .catch(() => {
          /* ignore */
        });
    }
  });
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
